import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Cellar, CellarInvitation, Wine } from '../types';

interface ProfileTabProps {
  user: User;
  username: string | null;
  cellars: Cellar[];
  wines: Wine[];
  pendingInvitations: CellarInvitation[];
  onShowInvitations: () => void;
  onSignOut: () => void;
  onDeleteAccount: () => void;
  onUsernameUpdated: (username: string) => void;
}

export function ProfileTab({
  user,
  username,
  cellars,
  wines,
  pendingInvitations,
  onShowInvitations,
  onSignOut,
  onDeleteAccount,
  onUsernameUpdated
}: ProfileTabProps) {
  const [editingProfile, setEditingProfile] = useState(false);
  const [newUsername, setNewUsername] = useState(username || '');
  const [saving, setSaving] = useState(false);
  const [defaultCellarId, setDefaultCellarId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Load default cellar preference
  useEffect(() => {
    async function loadDefaultCellar() {
      const { data } = await supabase
        .from('user_profiles')
        .select('default_cellar_id')
        .eq('user_id', user.id)
        .single();
      if (data?.default_cellar_id) {
        setDefaultCellarId(data.default_cellar_id);
      }
    }
    loadDefaultCellar();
  }, [user.id]);

  // Calculate stats
  const totalWines = wines.length;
  const totalBottles = wines.reduce((sum, w) => sum + w.quantity, 0);
  const totalValue = wines.reduce((sum, w) => sum + (w.purchase_price || 0) * w.quantity, 0);
  const memberSince = new Date(user.created_at).toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          username: newUsername,
          default_cellar_id: defaultCellarId
        });
      onUsernameUpdated(newUsername);
      setEditingProfile(false);
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleExportToSheets = async () => {
    setExporting(true);
    try {
      // Generate CSV
      const headers = [
        'Wine Name', 'Producer', 'Vintage', 'Color', 'Type', 'Varietal',
        'Country', 'Region', 'Appellation', 'Quantity', 'Price', 'Cellar Location',
        'Drink From', 'Drink To', 'My Rating', 'Notes'
      ];
      
      const rows = wines.map(w => [
        w.wine_name || '',
        w.producer || '',
        w.vintage || '',
        w.color || '',
        w.wine_type || '',
        w.varietal || '',
        w.country || '',
        w.region || '',
        w.appellation || '',
        w.quantity || 0,
        w.purchase_price || '',
        w.cellar_location || '',
        w.drink_from || '',
        w.drink_to || '',
        w.my_rating || '',
        (w.tasting_notes || '').replace(/"/g, '""')
      ]);
      
      const csv = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cellar-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="main-content" style={{ background: 'var(--color-parchment)' }}>
      {/* Header with Avatar */}
      <div style={{ padding: '20px', textAlign: 'center' }}>
        {/* Avatar */}
        <div style={{
          width: '72px',
          height: '72px',
          borderRadius: '36px',
          background: 'var(--color-wine)',
          margin: '0 auto 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '28px',
          fontWeight: '700'
        }}>
          {(username || user.email || '?').charAt(0).toUpperCase()}
        </div>
        
        <h1 className="wine-title" style={{ 
          fontSize: '24px', 
          margin: '0 0 4px',
          color: 'var(--color-text)'
        }}>
          {username || user.email?.split('@')[0] || 'User'}
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: 0 }}>
          {user.email}
        </p>
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
          Member since {memberSince}
        </p>
      </div>

      {/* Stats Card */}
      <div style={{ padding: '0 16px 16px' }}>
        <div className="ios-card" style={{
          padding: '16px',
          display: 'flex',
          justifyContent: 'space-around'
        }}>
          <div style={{ textAlign: 'center' }}>
            <span className="wine-title" style={{ 
              fontSize: '22px', 
              color: 'var(--color-wine)',
              display: 'block'
            }}>
              {totalWines}
            </span>
            <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>Wines</span>
          </div>
          <div style={{ width: '1px', background: 'var(--color-border)' }} />
          <div style={{ textAlign: 'center' }}>
            <span className="wine-title" style={{ 
              fontSize: '22px', 
              color: 'var(--color-wine)',
              display: 'block'
            }}>
              {totalBottles}
            </span>
            <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>Bottles</span>
          </div>
          <div style={{ width: '1px', background: 'var(--color-border)' }} />
          <div style={{ textAlign: 'center' }}>
            <span className="wine-title" style={{ 
              fontSize: '22px', 
              color: 'var(--color-wine)',
              display: 'block'
            }}>
              ${totalValue >= 1000 ? `${(totalValue / 1000).toFixed(1)}k` : totalValue}
            </span>
            <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>Value</span>
          </div>
        </div>
      </div>

      {/* Settings List */}
      <div style={{ padding: '0 16px', paddingBottom: '100px' }}>
        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <div 
            className="ios-card" 
            style={{ marginBottom: '16px', overflow: 'hidden', cursor: 'pointer' }}
            onClick={onShowInvitations}
          >
            <div className="ios-list-row" style={{ 
              background: '#fef3c7',
              padding: '14px 16px'
            }}>
              <span style={{ fontSize: '20px' }}>✉️</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: '16px', color: 'var(--color-text)', fontWeight: '600' }}>
                  Pending Invitations
                </span>
                <span style={{ 
                  fontSize: '14px', 
                  color: '#d97706',
                  display: 'block'
                }}>
                  {pendingInvitations.length} cellar{pendingInvitations.length > 1 ? 's' : ''} want you to join
                </span>
              </div>
              <span style={{ color: 'var(--color-border)', fontSize: '18px' }}>›</span>
            </div>
          </div>
        )}

        {/* Main Settings */}
        <div className="ios-card" style={{ overflow: 'hidden', marginBottom: '16px' }}>
          {/* Edit Profile */}
          <div 
            className="ios-list-row"
            onClick={() => setEditingProfile(true)}
            style={{ cursor: 'pointer', padding: '14px 16px' }}
          >
            <span style={{ fontSize: '20px' }}>✏️</span>
            <span style={{ flex: 1, fontSize: '16px', color: 'var(--color-text)' }}>
              Edit Profile
            </span>
            <span style={{ color: 'var(--color-border)', fontSize: '18px' }}>›</span>
          </div>
          
          {/* Default Cellar */}
          <div className="ios-list-row" style={{ padding: '14px 16px' }}>
            <span style={{ fontSize: '20px' }}>🍷</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: '16px', color: 'var(--color-text)', display: 'block' }}>
                Default Cellar
              </span>
              <select
                value={defaultCellarId || ''}
                onChange={async (e) => {
                  const newId = e.target.value || null;
                  setDefaultCellarId(newId);
                  await supabase
                    .from('user_profiles')
                    .upsert({
                      user_id: user.id,
                      default_cellar_id: newId
                    });
                }}
                style={{
                  fontSize: '13px',
                  color: 'var(--color-text-muted)',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  marginTop: '2px'
                }}
              >
                <option value="">None</option>
                {cellars.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Export Data */}
          <div 
            className="ios-list-row"
            onClick={handleExportToSheets}
            style={{ cursor: 'pointer', padding: '14px 16px' }}
          >
            <span style={{ fontSize: '20px' }}>📤</span>
            <span style={{ flex: 1, fontSize: '16px', color: 'var(--color-text)' }}>
              {exporting ? 'Exporting...' : 'Export to CSV'}
            </span>
            <span style={{ color: 'var(--color-border)', fontSize: '18px' }}>›</span>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="ios-card" style={{ overflow: 'hidden' }}>
          {/* Sign Out */}
          <div 
            className="ios-list-row"
            onClick={onSignOut}
            style={{ cursor: 'pointer', padding: '14px 16px' }}
          >
            <span style={{ fontSize: '20px' }}>🚪</span>
            <span style={{ flex: 1, fontSize: '16px', color: 'var(--color-text)' }}>
              Sign Out
            </span>
          </div>
          
          {/* Delete Account */}
          <div 
            className="ios-list-row"
            onClick={() => setConfirmDelete(true)}
            style={{ cursor: 'pointer', padding: '14px 16px' }}
          >
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <span style={{ flex: 1, fontSize: '16px', color: 'var(--color-danger)' }}>
              Delete Account
            </span>
          </div>
        </div>

        {/* Build Version Footer */}
        <div style={{ 
          padding: '20px 16px 40px',
          textAlign: 'center'
        }}>
          <p style={{ 
            fontSize: '11px', 
            color: 'var(--color-text-muted)',
            opacity: 0.6
          }}>
            Build: {new Date(__BUILD_TIME__).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {editingProfile && (
        <div className="modal-overlay" onClick={() => setEditingProfile(false)}>
          <div 
            className="ios-sheet"
            onClick={e => e.stopPropagation()}
            style={{ width: '100%' }}
          >
            <div className="ios-sheet-handle" />
            
            <div style={{ 
              padding: '0 20px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <button
                onClick={() => setEditingProfile(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-wine)',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <h2 className="wine-title" style={{ 
                fontSize: '18px', 
                margin: 0,
                color: 'var(--color-text)'
              }}>
                Edit Profile
              </h2>
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="ios-btn ios-btn-primary"
                style={{ padding: '8px 16px', fontSize: '15px' }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>

            <div style={{ padding: '0 20px 20px' }}>
              <label className="ios-label">Username</label>
              <input
                type="text"
                value={newUsername}
                onChange={e => setNewUsername(e.target.value)}
                className="ios-input"
                placeholder="Choose a username"
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(false)}>
          <div 
            className="ios-sheet"
            onClick={e => e.stopPropagation()}
            style={{ width: '100%' }}
          >
            <div className="ios-sheet-handle" />
            
            <div style={{ padding: '0 20px 20px', textAlign: 'center' }}>
              <span style={{ fontSize: '48px' }}>⚠️</span>
              <h2 className="wine-title" style={{ 
                fontSize: '20px', 
                margin: '12px 0 8px',
                color: 'var(--color-text)'
              }}>
                Delete Account?
              </h2>
              <p style={{ 
                color: 'var(--color-text-muted)', 
                marginBottom: '20px',
                fontSize: '15px'
              }}>
                This will permanently delete your account and all your data. This cannot be undone.
              </p>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="ios-btn ios-btn-secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  onClick={onDeleteAccount}
                  className="ios-btn"
                  style={{ flex: 1, background: 'var(--color-danger)', color: 'white' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
